import { flattenScoreWire } from 'components/ReceptionScore/flattenWire';
import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useAuthorization } from 'components/Authorization';
import {
  MsgType,
  mustache2SlateValuePolicyMsgTypes,
  SlateValue2Mustache,
  SlateValue2MustacheV2,
} from 'components/TemplateEditor/mustacheUtils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import { Descendant } from 'slate';
import { cancellingTradeScenarios } from '../config';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { recommendTemplates, RecommendTemplatesParams } from 'views/Aide/service';
import { useMount } from 'ahooks';
import { useFormikContext } from 'formik';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { newScenarioTypes } from '@knowledge/Activity/PolicyForm/share';

export interface DescendantExecuteContext {
  content: Descendant[];
  translatedContent: Descendant[];
}

export interface PolicyFormValues
  extends Omit<
    Marketing.Policy,
    | 'id'
    | 'taskId'
    | 'roundId'
    | 'priority'
    | 'bilingualExecuteContext'
    | 'bilingualExecuteContexts'
    | 'regionExecuteContexts'
    | 'executeItemContext'
    | 'cancelTradeConfig'
  > {
  id?: Marketing.Policy['id'];
  bilingualExecuteContext: DescendantExecuteContext;
  bilingualExecuteContexts: DescendantExecuteContext[];
  regionExecuteContexts?: {
    enable: boolean;
    region: General.UpperCaseRegion;
    executeContext: DescendantExecuteContext;
  }[];
  regions?: General.UpperCaseRegion[];
  executeItemContext: {
    itemId: string;
    customizeContext?: Descendant[];
  }[];
  cancelTradeConfig?: Omit<Required<Marketing.Policy>['cancelTradeConfig'], 'contents'> & {
    contents: (Omit<
      Util.ElementType<Required<Marketing.Policy>['cancelTradeConfig']['contents']>,
      'executeContext'
    > & {
      executeContext: DescendantExecuteContext;
    })[];
  };
}

export type FV = PolicyFormValues;

export interface Validate {
  (errors: Partial<Record<keyof FV, any>>, values: FV, t: TFnType): void;
}

export const Question = styled(QuestionCircleOutlined)`
  margin-left: 4px;
  cursor: help;
`;

export const InputWrapper = styled.div`
  width: 320px;
  > * {
    width: 100%;
  }
`;

export const useShippingStatusLabel = () => {
  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();

  const displayExtraText = systemLanguage === 'zh-CN';

  const statusLabelMap: Record<Marketing.ShippingStatus, string> = {
    TO_PROCESS: `${t('review.处理中')}${displayExtraText ? ' To Process' : ''}`,
    PROCESSED: `${t('review.已处理')}${displayExtraText ? ' Processed' : ''}`,
  };

  return statusLabelMap;
};

export const getSlateContent = (mustache: string): Descendant[] => {
  let slateValue: Descendant[] = mustache2SlateValuePolicyMsgTypes('');

  try {
    slateValue = mustache2SlateValuePolicyMsgTypes(mustache.replace(/^#mustache#/, ''));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }

  return slateValue;
};

export const getSlateExecuteContext = (
  executeContext: Marketing.ExecuteContext,
): Marketing.ExecuteContext<Descendant[]> => {
  const { content, translatedContent } = executeContext;
  return {
    content: getSlateContent(flattenScoreWire(content || '')),
    translatedContent: getSlateContent(flattenScoreWire(translatedContent || '')),
  };
};

export const getMustacheContent = (slateValue: Descendant[]): string => {
  return `#mustache#${SlateValue2Mustache(slateValue)}`;
};

export const getMustacheExecuteContext = (
  executeContext: Marketing.ExecuteContext<Descendant[]>,
): Marketing.ExecuteContext => {
  const { content, translatedContent } = executeContext;

  return {
    content: getMustacheContent(content),
    translatedContent: getMustacheContent(translatedContent),
  };
};

/** 策略 发送内容 多消息类型适配 */
export const getMustacheExecuteContextV2 = (
  executeContext: Marketing.ExecuteContext<Descendant[]>,
): Marketing.ExecuteContext => {
  const { content, translatedContent } = executeContext;
  return {
    content: `#mustache#${SlateValue2MustacheV2(content)}`,
    translatedContent: `#mustache#${SlateValue2MustacheV2(translatedContent)}`,
  };
};

export const isSupportItemScenario = (
  scenarioType: Marketing.AllScenarioType,
  isNewPolicy = false,
) =>
  isNewPolicy &&
  ((
    [
      'TIMEOUT_REPLY',
      'WELCOME_REPLY',
      'ORDER_REMINDER',
      'CONFIRM_TRADE',
      'SIGN_REMINDER',
      'POSITIVE_REVIEW_CARE',
    ] as const
  ).includes(scenarioType) ||
    newScenarioTypes.includes(scenarioType));

export const supportLogisticsScenarios = [
  'SHIPPED_REMINDER',
  'DELIVERY_REMINDER',
  'WAIT_DELIVERY_AGAIN',
] as const;

export function getMsgTypes(
  platform: General.PlatformUpperCase,
  scenarioType: Marketing.AllScenarioType,
  orgId?: number,
  isNewPolicy?: boolean,
): MsgType[] {
  if (scenarioType === 'POSITIVE_REVIEW_REPLY' || scenarioType === 'NEGATIVE_REVIEW_REPLY') {
    return ['text'];
  }

  if (platform === 'TOKOPEDIA') {
    if (scenarioType === 'QUICK_PHRASE') {
      return ['text'];
    }

    return ['text', 'image'];
  }

  if (platform === 'ALIEXPRESS') {
    if (scenarioType === 'QUICK_PHRASE') {
      return ['text'];
    }

    return ['text', 'image'];
  }

  if (isTiKTokUpperCase(platform)) {
    const tikTokMsgTypes: MsgType[] = ['text', 'image', 'video'];

    if (scenarioType === 'POSITIVE_REVIEW_CARE' || scenarioType === 'INVITE_FOLLOWER') {
      tikTokMsgTypes.push('follow');
    }

    if (isSupportItemScenario(scenarioType, isNewPolicy)) {
      tikTokMsgTypes.push('item');
    }

    if (supportLogisticsScenarios.includes(scenarioType)) {
      tikTokMsgTypes.push('logistics');
    }

    return tikTokMsgTypes;
  }

  if (platform === 'FACEBOOK') {
    return ['text', 'image', 'video'];
  }

  if (scenarioType === 'POSITIVE_REVIEW_CARE' || scenarioType === 'INVITE_FOLLOWER') {
    if (isSupportItemScenario(scenarioType, isNewPolicy)) {
      return ['text', 'sticker', 'image', 'video', 'follow', 'item'];
    }
    return ['text', 'sticker', 'image', 'video', 'follow'];
  }

  if (scenarioType === 'OUT_STOCK_REMINDER') {
    return ['text', 'image', 'sticker'];
  }

  const isCancellingTrade = cancellingTradeScenarios.includes(
    scenarioType as Marketing.CancellingTradeScenarioType,
  );

  if (isCancellingTrade) {
    return ['text', 'sticker', 'image'];
  }

  if (
    scenarioType === 'CONFIRM_TRADE' &&
    (orgId === 8371 || orgId === 1002 || import.meta.env.VITE_ENV === 'feature') // 8371 唯一开通的客户 org，1002 测试 org。
  ) {
    if (isSupportItemScenario(scenarioType, isNewPolicy)) {
      return ['text', 'sticker', 'image', 'video', 'qrcode', 'item'];
    }
    return ['text', 'sticker', 'image', 'video', 'qrcode'];
  }

  if (isSupportItemScenario(scenarioType, isNewPolicy)) {
    return platform === 'KWAI'
      ? ['text', 'image', 'video', 'item']
      : ['text', 'sticker', 'image', 'video', 'item'];
  }

  return platform === 'KWAI'
    ? ['text', 'image', 'video']
    : ['text', 'sticker', 'image', 'video'];
}

export function useGetMsgTypes() {
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const { orgId } = payload || {};

  const { whiteListConfig: { AIDE_REMOVE_TASK } = {} } = useOrgConfigStateValue();

  return useCallback(
    (platform: General.PlatformUpperCase, scenarioType: Marketing.AllScenarioType) => {
      return getMsgTypes(platform, scenarioType, orgId, AIDE_REMOVE_TASK);
    },
    [orgId, AIDE_REMOVE_TASK],
  );
}

export function useMsgTypes(
  platform: General.PlatformUpperCase,
  scenarioType: Marketing.AllScenarioType,
) {
  const getMsgTypes = useGetMsgTypes();

  return useMemo(
    () => getMsgTypes(platform, scenarioType),
    [platform, scenarioType, getMsgTypes],
  );
}

export function findFirstError(
  e: React.MouseEvent<HTMLElement, MouseEvent>,
  scrollIntoView = true,
) {
  const findFooterElement = (target: any): undefined | HTMLElement => {
    if (!target) {
      return undefined;
    }

    const { tagName, parentElement } = target;

    if (tagName === 'FOOTER') {
      return target as HTMLElement;
    }

    return findFooterElement(parentElement);
  };

  const section = findFooterElement(e.target)?.parentElement;

  if (!section) {
    return undefined;
  }

  const findFirstError = (section: HTMLElement) => {
    const errorElement =
      section.querySelector('.ant-form-item-explain-error') ||
      section.querySelector('.ant-tag-error');

    if (errorElement) {
      if (scrollIntoView) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return errorElement.textContent;
    }

    return undefined;
  };

  return findFirstError(section);
}

export function useLangTemplatesMap(params: RecommendTemplatesParams) {
  const { values: { knowledgeId } = {} } = useFormikContext<any>();

  const [langTemplatesMap, setLangTemplatesMap] = useState<Aide.LangTemplatesMap>();

  useMount(() => {
    if (knowledgeId) {
      return;
    }

    recommendTemplates(params)
      .then(({ langTemplateMap }) => {
        setLangTemplatesMap(langTemplateMap);
      })
      .catch(() => {});
  });

  return langTemplatesMap;
}
